import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';

import { Box, CardContent, FormError } from '@/shared/ui';
import { Button } from '@/shared/ui/lib/Button';
import { SvgIcon } from '@/shared/ui/SvgIcon';
import { ControlledTextField } from '@/shared/ui/TextField';
import { CustomTypography } from '@/shared/ui/Typography';

import { createForgotPasswordSchema } from '../model/schemas';
import { TForgotPasswordData } from '../model/types';
import { useForgotPassword } from '../model/useForgotPassword';

type TForgotPasswordForm = { onSubmited?: (data: TForgotPasswordData) => void };

export const FORGOT_PASSWORD_FORM_ID = 'forgot-password-form';

export const ForgotPasswordForm = ({ onSubmited }: TForgotPasswordForm) => {
  const { t } = useTranslation();

  const { forgotPassword, isPending, isError, error } = useForgotPassword();

  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm({
    resolver: zodResolver(createForgotPasswordSchema(t)),
    defaultValues: {
      email: '',
    },
    mode: 'onTouched',
  });

  const onSubmit = handleSubmit(async (data) => {
    await forgotPassword(data, onSubmited);
  });

  return (
    <Box
      component="form"
      id={FORGOT_PASSWORD_FORM_ID}
      onSubmit={onSubmit}
      sx={{ width: '100%', maxWidth: '450px' }}
    >
      <CardContent sx={{ textAlign: 'center', px: 0 }}>
        <CustomTypography variant="h5" sx={{ fontWeight: 600 }}>
          {t('auth:forgotPassword.title')}
        </CustomTypography>
      </CardContent>

      <CardContent sx={{ px: 0, py: 1 }}>
        <ControlledTextField
          name="email"
          control={control}
          type="email"
          placeholder={t('common:inputs.placeholders.email')}
          disabled={isPending}
          error={!!errors.email}
          helperText={errors.email?.message}
          autoFocus
          startAdornment={<SvgIcon name="mail-icon" />}
        />
      </CardContent>

      {isError && (
        <FormError message={error?.message || t('common:errors.generic')} />
      )}

      <CardContent sx={{ px: 0, pt: 1, pb: 0, textAlign: 'center' }}>
        <Button
          form={FORGOT_PASSWORD_FORM_ID}
          type="submit"
          variant="contained"
          color="primary"
          disabled={!isValid || isPending}
          fullWidth
          loading={isPending}
        >
          {t('auth:forgotPassword.getMail')}
        </Button>
      </CardContent>
    </Box>
  );
};
